import objectID from 'bson-objectid';
import { isEqual } from 'lodash';
import browserHistory from '../../../browserHistory';
import * as projects from '../../../firebase/projects';
import { addExample, isExample } from '../../../firebase/examples';
import { getSharedSketch, saveSharedSketch } from '../../../firebase/sharing';
import { copyFile } from '../../../firebase/storage';
import { refreshAuthUser } from '../../../firebase/auth';
import * as ActionTypes from '../../../constants';
import { incrementAssetTotalSize, adjustTotalSize } from './assets';
import { showToast, setToastText } from './toast';
import {
  setUnsavedChanges,
  setRemoteEditNotice,
  justOpenedProject,
  resetJustOpenedProject,
  showErrorModal,
  setPreviousPath,
  stopSketch
} from './ide';
import { clearLocalBackup } from '../utils/localBackup';
import { runWithBusyToast } from '../utils/busyToast';
import { clearState, saveState } from '../../../persistState';
import { getIsStaff, getIsUserOwner } from '../selectors/users';
import { watchArSessionValues } from './arSession';

export function setProject(project) {
  return {
    type: ActionTypes.SET_PROJECT,
    project,
    files: project.files,
    owner: project.user,
    visibility: project.visibility
  };
}

export function setProjectName(name) {
  return {
    type: ActionTypes.SET_PROJECT_NAME,
    name
  };
}

export function projectSaveFail(error) {
  return {
    type: ActionTypes.PROJECT_SAVE_FAIL,
    error
  };
}

export function setNewProject(project) {
  return {
    type: ActionTypes.NEW_PROJECT,
    project,
    owner: project.user,
    files: project.files
  };
}

export function getProject(id, username) {
  return (dispatch) => {
    dispatch(justOpenedProject());
    dispatch({ type: ActionTypes.SET_PROJECT_NOT_FOUND, value: false });
    return projects
      .getProject(username, id)
      .then((data) => {
        dispatch(setProject(data));
        dispatch(setUnsavedChanges(false));
        // Start watching this project's AR session doc (if any) so the preview's sliders
        // load their stored Firebase values on the first Run — without needing to open the
        // AR modal. No-op when the project has no arSessionId.
        dispatch(watchArSessionValues());
        // Resolve whether this sketch is a published AR example — narrows editing to admins
        // (selectCanEditSketch). Best-effort: a failed check leaves it non-example (read gate
        // still applies from ownership). SET_PROJECT already cleared any prior value.
        isExample(id)
          .then((flag) =>
            dispatch({
              type: ActionTypes.SET_PROJECT_IS_EXAMPLE,
              value: flag
            })
          )
          .catch(() => {});
      })
      .catch((error) => {
        // Private/non-owned or non-existent sketch: flag it so the editor shows a clear
        // "private or doesn't exist" message instead of a silent blank default editor.
        dispatch({ type: ActionTypes.SET_PROJECT_NOT_FOUND, value: true });
      });
  };
}

/**
 * Apply a live Firestore snapshot of the open project (instructor edit → student sees it).
 *
 * Returns the applied files array (so the caller can push them into CodeMirror, which does not
 * re-render the open buffer on its own) or `null` when the snapshot was ignored/skipped. Rules:
 * - Ignore snapshots for a different project, our own write echo, or anything not strictly newer
 *   than the local copy (so the first snapshot after load and self-saves are no-ops).
 * - Local edits win: if the editor has unsaved changes or is mid-save, skip — never clobber
 *   in-progress work. `updatedAt` is left untouched so the existing optimistic-concurrency guard
 *   still shows the "stale — reload" modal if the reader later saves over the foreign change.
 * - Otherwise apply the fresh copy; if the reader is the owner and the edit was foreign, flag the
 *   student banner.
 */
export function receiveProjectSnapshot(data) {
  return (dispatch, getState) => {
    const state = getState();
    if (!data || data.id !== state.project.id) return null;
    const known = state.project.updatedAt || 0;
    if (!(data.updatedAt > known)) return null;
    if (data.lastEditedBy && data.lastEditedBy === state.user.id) return null;
    if (state.ide.unsavedChanges || state.project.isSaving) return null;

    dispatch(setProject(data));
    dispatch(setUnsavedChanges(false));

    // Owner (student) sees a foreign edit → show the "your instructor updated this sketch" banner.
    if (getIsUserOwner(state) && data.lastEditedBy) {
      dispatch(setRemoteEditNotice(true));
    }
    return data.files;
  };
}

export function persistState() {
  return (dispatch, getState) => {
    dispatch({
      type: ActionTypes.PERSIST_STATE
    });
    const state = getState();
    saveState(state);
  };
}

export function clearPersistedState() {
  return (dispatch) => {
    dispatch({
      type: ActionTypes.CLEAR_PERSISTED_STATE
    });
    clearState();
  };
}

export function startSavingProject() {
  return {
    type: ActionTypes.START_SAVING_PROJECT
  };
}

export function endSavingProject() {
  return {
    type: ActionTypes.END_SAVING_PROJECT
  };
}

export function projectSaveSuccess() {
  return {
    type: ActionTypes.PROJECT_SAVE_SUCCESS
  };
}

// want a function that will check for changes on the front end
function getSynchedProject(currentState, responseProject) {
  let hasChanges = false;
  const synchedProject = Object.assign({}, responseProject);
  const currentFiles = currentState.files.map(
    ({ name, children, content }) => ({ name, children, content })
  );
  const responseFiles = responseProject.files.map(
    ({ name, children, content }) => ({ name, children, content })
  );
  if (!isEqual(currentFiles, responseFiles)) {
    synchedProject.files = currentState.files;
    hasChanges = true;
  }
  if (currentState.project.name !== responseProject.name) {
    synchedProject.name = currentState.project.name;
    hasChanges = true;
  }
  return {
    synchedProject,
    hasChanges
  };
}

export function saveProject(
  selectedFile = null,
  autosave = false,
  mobile = false,
  retried = false
) {
  return (dispatch, getState) => {
    const state = getState();
    if (state.project.isSaving) {
      return Promise.resolve();
    }
    dispatch(startSavingProject());
    const isOwner =
      !state.project.owner || state.project.owner.id === state.user.id;
    // Staff (instructor/admin) may edit any student's sketch in place. Like the owner, they write
    // directly via projects.updateProject (Firestore rules allow the staff write) — not through the
    // share-link Cloud Function. Autosave follows the same path, so their edits persist like an owner's.
    const isStaffEditor = getIsStaff(state);
    // A non-owner non-staff user may save ONLY when this session was opened via an edit-capability
    // share link; that save is routed through the saveSharedSketch Cloud Function (token-validated
    // server-side).
    const isSharedEdit =
      state.ide.shareCapability === 'edit' && !!state.ide.shareToken;
    // Owner and staff both save directly to the project doc.
    const canSaveDirect = isOwner || isStaffEditor;
    if (state.user.id && !canSaveDirect && !isSharedEdit) {
      return Promise.reject();
    }
    const files = [...state.files];

    // Autosave stays silent — a toast blinking every few seconds would be worse than none. A manual
    // save gets the busy toast only if it outlasts the grace period, so a fast save is still silent.
    // The completion message ('Toast.SketchSaved'/'Toast.SketchFailedSave') is dispatched inside the
    // chain below and supersedes it.
    const withSavingToast = (promise) =>
      autosave
        ? promise
        : dispatch(runWithBusyToast('Toast.Saving', () => promise));

    if (selectedFile) {
      const fileToUpdate = files.find((file) => file.id === selectedFile.id);
      fileToUpdate.content = selectedFile.content;
    }

    const changes = {
      name: state.project.name,
      files,
      // New sketches are Private by default (e.g. student work shouldn't be world-readable on creation).
      // The owner can switch a saved sketch to Public afterward. Existing sketches keep their visibility.
      visibility: state.project.id ? state.project.visibility : 'Private'
    };

    if (state.project.id) {
      // Owner and staff save directly; a shared editor saves via the token-validated Cloud Function.
      const savePromise = canSaveDirect
        ? projects.updateProject(
            state.project.id,
            changes,
            state.project.updatedAt,
            // Adopt-on-save: hand the firebase layer the classroom to stamp an as-yet-unstamped
            // sketch with — but ONLY for the owner. A staff member editing a student's sketch must
            // not stamp it with their own classroom (updateProject also guards this on the stored
            // userId; this is the belt to that braces).
            { classroomId: isOwner ? state.user.currentClassroomId || '' : '' }
          )
        : saveSharedSketch(
            state.project.id,
            state.ide.shareToken,
            changes,
            state.project.updatedAt
          ).then((r) => r.project);
      return withSavingToast(
        savePromise
          .then((data) => {
            dispatch(endSavingProject());
            dispatch(setUnsavedChanges(false));
            // Clear the localStorage backup after successful server save (#3891)
            clearLocalBackup(state.project.id);
            const { hasChanges, synchedProject } = getSynchedProject(
              getState(),
              data
            );
            if (hasChanges) {
              dispatch(setUnsavedChanges(true));
            }
            dispatch(setProject(synchedProject));
            dispatch(projectSaveSuccess());
            if (!autosave) {
              if (state.ide.justOpenedProject && state.preferences.autosave) {
                dispatch(showToast(5500));
                dispatch(setToastText('Toast.SketchSaved'));
                setTimeout(
                  () => dispatch(setToastText('Toast.AutosaveEnabled')),
                  1500
                );
                dispatch(resetJustOpenedProject());
              } else {
                dispatch(showToast(1500));
                dispatch(setToastText('Toast.SketchSaved'));
              }
            }
          })
          .catch((error) => {
            dispatch(endSavingProject());
            dispatch(setToastText('Toast.SketchFailedSave'));
            dispatch(showToast(1500));
            const code = error?.code || '';
            if (
              code === 'forbidden' ||
              code === 'functions/permission-denied'
            ) {
              dispatch(showErrorModal('staleSession'));
            } else if (
              code === 'stale' ||
              error?.message === 'stale' ||
              code === 'functions/failed-precondition'
            ) {
              dispatch(showErrorModal('staleProject'));
            } else {
              dispatch(projectSaveFail(error?.message));
            }
          })
      );
    }

    return withSavingToast(
      projects
        .createProject(changes)
        .then((data) => {
          dispatch(endSavingProject());
          const { hasChanges, synchedProject } = getSynchedProject(
            getState(),
            data
          );

          dispatch(setNewProject(synchedProject));
          dispatch(setUnsavedChanges(false));
          browserHistory.push(`/${data.user.username}/sketches/${data.id}`);

          if (hasChanges) {
            dispatch(setUnsavedChanges(true));
          }

          dispatch(projectSaveSuccess());
          if (!autosave) {
            if (state.preferences.autosave) {
              dispatch(showToast(5500));
              dispatch(setToastText('Toast.SketchSaved'));
              setTimeout(
                () => dispatch(setToastText('Toast.AutosaveEnabled')),
                1500
              );
              dispatch(resetJustOpenedProject());
            } else {
              dispatch(showToast(1500));
              dispatch(setToastText('Toast.SketchSaved'));
            }
          }
        })
        .catch(async (error) => {
          dispatch(endSavingProject());
          if (error.code === 'forbidden') {
            dispatch(setToastText('Toast.SketchFailedSave'));
            dispatch(showToast(1500));
            dispatch(showErrorModal('staleSession'));
            return undefined;
          }
          if (error.code === 'permission-denied') {
            // The only rule that denies a project create is the email-verified hard block. The token's
            // email_verified claim is stale in an open session, so a user who just verified still has
            // the old claim. Refresh the ID token and retry once before blaming the user.
            if (!retried) {
              await refreshAuthUser(dispatch);
              await dispatch(saveProject(selectedFile, autosave, mobile, true));
              return undefined;
            }
            // Refresh didn't help — the email genuinely isn't verified yet.
            dispatch(showToast(6000));
            dispatch(
              setToastText(
                'Please verify your email before you can save. Check your inbox for the verification link.'
              )
            );
            return undefined;
          }
          dispatch(setToastText('Toast.SketchFailedSave'));
          dispatch(showToast(1500));
          dispatch(projectSaveFail(error?.message));
          return undefined;
        })
    );
  };
}

export function autosaveProject(mobile = false) {
  return (dispatch, getState) => {
    saveProject(null, true, mobile)(dispatch, getState);
  };
}

export function resetProject() {
  return (dispatch) => {
    // RESET_PROJECT alone only flips `ide.isPlaying` back to false in the store. The preview
    // iframe keeps its OWN play state and tears the sketch down only when it receives a STOP
    // message — so without this the previous sketch keeps running and rendering after the
    // project is reset (File > New, deleting the open sketch, logging out).
    dispatch(stopSketch());
    dispatch({ type: ActionTypes.RESET_PROJECT });
  };
}

export function newProject() {
  browserHistory.push('/', { confirmed: true });
  return resetProject();
}

function generateNewIdsForChildren(file, files) {
  const newChildren = [];
  file.children.forEach((childId) => {
    const child = files.find((childFile) => childFile.id === childId);
    const newId = objectID().toHexString();
    child.id = newId;
    child._id = newId;
    newChildren.push(newId);
    generateNewIdsForChildren(child, files);
  });
  file.children = newChildren; // eslint-disable-line
}

async function cloneProjectWork(project, dispatch, getState) {
  dispatch(setUnsavedChanges(false));
  const state = getState();
  // Sketch-list rows pass a metadata-only project (listProjects omits `files`). Fetch the full
  // doc so the clone actually copies the sketch's files instead of throwing on `undefined.map`.
  // Fetch under the ROW's ownerUsername when present (an AR example is owned by an admin, not the
  // current user) — falling back to the current user for own-sketch rows that omit it.
  let sourceProject = project;
  if (project && !project.files) {
    sourceProject = await projects.getProject(
      project.ownerUsername || state.user.username,
      project.id
    );
  }
  const files = sourceProject ? sourceProject.files : state.files;
  const projectName = sourceProject ? sourceProject.name : state.project.name;
  const newFiles = files.map((file) => ({ ...file }));

  // generate new IDS for all files
  const rootFile = newFiles.find((file) => file.name === 'root');
  const newRootFileId = objectID().toHexString();
  rootFile.id = newRootFileId;
  rootFile._id = newRootFileId;
  generateNewIdsForChildren(rootFile, newFiles);

  // Copy each media asset to a new Storage object owned by the current user
  // so the clone is independent from the original. If a copy fails, keep the
  // original url rather than failing the whole clone.
  const uid = getState().user.id;
  // not signed in -> cannot own copied assets; abort the clone (consistent promise return)
  if (!uid) return Promise.resolve();
  let copiedBytes = 0;
  await Promise.all(
    newFiles.map(async (file) => {
      if (!file.url) return;
      try {
        const copied = await copyFile(uid, file.url);
        file.url = copied.url; // eslint-disable-line no-param-reassign
        copiedBytes += copied.size || 0;
      } catch (error) {
        console.warn(
          `Failed to copy media for ${file.name}; clone will reference the original.`,
          error
        );
      }
    })
  );
  // Account the copied media against the user's cumulative quota (clones previously bypassed it
  // entirely): bump the optimistic Redux total and persist to users/{uid}.totalSize.
  if (copiedBytes > 0) {
    dispatch(incrementAssetTotalSize(copiedBytes));
    dispatch(adjustTotalSize(copiedBytes));
  }

  return projects
    .cloneProject({ name: `${projectName} copy`, files: newFiles })
    .then((res) => {
      browserHistory.push(`/${res.user.username}/sketches/${res.id}`);
      dispatch(setNewProject(res));
      dispatch(showToast('Toast.SketchDuplicated'));
    })
    .catch((error) => {
      dispatch({
        type: ActionTypes.PROJECT_SAVE_FAIL,
        error: error?.message
      });
      dispatch(showToast('Toast.SketchFailedDuplicate', 5500));
    });
}

/**
 * Duplicate a sketch. One of the slowest things the editor does — it fetches the full source doc,
 * then copies EVERY media asset to a new Storage object, then creates the project, then navigates —
 * so the WHOLE body runs under the busy toast, not just the final write. Both call sites (the
 * sketch-list row dropdown and File ▸ Duplicate) go through here, so they share the feedback.
 */
export function cloneProject(project) {
  return (dispatch, getState) =>
    dispatch(
      runWithBusyToast('Toast.Duplicating', () =>
        cloneProjectWork(project, dispatch, getState)
      )
    );
}

export function setProjectSavedTime(updatedAt) {
  return {
    type: ActionTypes.SET_PROJECT_SAVED_TIME,
    value: updatedAt
  };
}

/**
 * Load a sketch opened via a capability share link (functions/getSharedSketch validates the token).
 * Works for Public AND Private sketches and records the token + capability so saveProject can route an
 * edit-capable session through the saveSharedSketch function.
 */
export function getSharedProject(id, token) {
  return async (dispatch) => {
    dispatch(justOpenedProject());
    dispatch({ type: ActionTypes.SET_PROJECT_NOT_FOUND, value: false });
    try {
      const { capability, project } = await getSharedSketch(id, token);
      // Record the capability BEFORE setting the project. These dispatches run after an await, so
      // React 17 does not batch them: if setProject (visibility='Private') landed first, there would be
      // one render with the Private project but no shareCapability yet — ProtectedSketchRoute would read
      // hasAccess=false and redirect a valid recipient to "/". Setting the capability first closes that gap.
      dispatch({ type: ActionTypes.SET_SHARE_ACCESS, token, capability });
      dispatch(setProject(project));
      dispatch(setUnsavedChanges(false));
    } catch (error) {
      dispatch({ type: ActionTypes.SET_PROJECT_NOT_FOUND, value: true });
    }
  };
}

export function changeProjectName(id, newName) {
  return (dispatch, getState) => {
    const state = getState();
    return dispatch(
      runWithBusyToast('Toast.Renaming', () =>
        projects
          .updateProject(id, { name: newName })
          .then((data) => {
            dispatch({
              type: ActionTypes.RENAME_PROJECT,
              payload: { id: data.id, name: data.name }
            });
            if (state.project.id === data.id) {
              dispatch({
                type: ActionTypes.SET_PROJECT_NAME,
                name: data.name
              });
              // Advance the open project's known updatedAt too. updateProject bumps the server
              // timestamp; without this the next save sends a stale knownUpdatedAt and trips the
              // updatedAt precondition -> a false "staleProject" conflict modal. (Mirrors
              // changeVisibility.)
              dispatch(setProjectSavedTime(data.updatedAt));
            }
          })
          .catch((error) => {
            dispatch({
              type: ActionTypes.PROJECT_SAVE_FAIL,
              error: error?.message
            });
            dispatch(showToast('Toast.SketchFailedRename', 5500));
          })
      )
    );
  };
}

export function deleteProject(id) {
  return (dispatch, getState) =>
    dispatch(
      runWithBusyToast('Toast.Deleting', () =>
        projects
          .deleteProject(id)
          .then(() => {
            const state = getState();
            if (id === state.project.id) {
              dispatch(resetProject());
              dispatch(setPreviousPath('/'));
            }
            dispatch({
              type: ActionTypes.DELETE_PROJECT,
              id
            });
            dispatch(showToast('Toast.SketchDeleted'));
          })
          .catch((error) => {
            if (error.code === 'forbidden') {
              dispatch(showErrorModal('staleSession'));
            } else {
              // ActionTypes.ERROR has no reducer (silent failure); surface a toast instead.
              dispatch(
                showToast(
                  'Could not delete the sketch. Please try again.',
                  5500
                )
              );
            }
          })
      )
    );
}
/**
 * Admin: register the currently-open project as an AR example. Examples must be Public so anyone
 * (including logged-out visitors browsing /examples) can open them, so this flips visibility to Public
 * first if needed, then adds the project id to the `examples` collection. The menu item is gated to an
 * admin who owns the open, saved sketch (see Nav.jsx), so this is the happy path; it guards against an
 * unsaved project defensively. After this, only admins can edit the sketch (selectCanEditSketch + rules).
 */
export function saveToExamples() {
  return (dispatch, getState) => {
    const state = getState();
    const projectId = state.project.id;
    if (!projectId) return Promise.resolve();
    return dispatch(
      runWithBusyToast('Toast.SavingToExamples', async () => {
        try {
          if (state.project.visibility !== 'Public') {
            const data = await projects.changeVisibility(projectId, 'Public');
            dispatch({
              type: ActionTypes.SET_PROJECT_VISIBILITY,
              visibility: 'Public',
              updatedAt: data.updatedAt
            });
          }
          await addExample(projectId);
          dispatch({ type: ActionTypes.SET_PROJECT_IS_EXAMPLE, value: true });
          dispatch(showToast('Toast.SavedToExamples', 2500));
        } catch (error) {
          dispatch(showToast('Toast.SaveToExamplesFailed', 2500));
        }
      })
    );
  };
}

export function changeVisibility(projectId, projectName, visibility, t) {
  return (dispatch, getState) => {
    const state = getState();

    return dispatch(
      runWithBusyToast('Toast.UpdatingVisibility', () =>
        projects
          .changeVisibility(projectId, visibility)
          .then((data) => {
            const { visibility: newVisibility, updatedAt, id } = data;

            dispatch({
              type: ActionTypes.CHANGE_VISIBILITY,
              payload: {
                id,
                visibility: newVisibility
              }
            });

            if (state.project.id === data.id) {
              dispatch({
                type: ActionTypes.SET_PROJECT_VISIBILITY,
                visibility: newVisibility,
                updatedAt
              });

              dispatch({
                type: ActionTypes.SET_PROJECT_NAME,
                name: data.name
              });
            }

            let visibilityLabel;

            switch (newVisibility) {
              case 'Public':
                visibilityLabel = t('Visibility.Public.Label');
                break;
              case 'Private':
                visibilityLabel = t('Visibility.Private.Label');
                break;
              default:
                visibilityLabel = newVisibility;
            }

            // Confirm OUTSIDE the "is this the open project?" branch. It used to sit inside, so
            // changing a sketch's visibility from a dashboard row completed in total silence.
            dispatch(
              showToast(
                t('Visibility.Changed', {
                  projectName,
                  newVisibility: visibilityLabel.toLowerCase()
                }),
                2000
              )
            );
          })
          .catch((error) => {
            // ActionTypes.ERROR has no reducer (silent failure); surface a toast instead.
            dispatch(
              showToast('Could not change visibility. Please try again.', 5500)
            );
          })
      )
    );
  };
}
